"use client"

import Box from "@mui/material/Box"
import Grid from "@mui/material/Grid"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import {
  AppContainer,
  Icons,
  Pagination,
  ProductCard,
  SectionContainer
} from "@src/components"
import { COLLECTION, PRODUCT_PAGE_SIZE } from "@src/constants/category"
import useRouterClient from "@src/hooks/use-router-client"
import EmptyProduct from "./empty-product"
import EmptyProductSale from "../home/empty-product"
import Filter from "./filter"
import ProductTitle from "./title"
import ProductListSkeleton from "./product-list-skeleton"
import { useParams } from "next/navigation"
import { useLayoutContext } from "@src/layouts/provider"
import { useTranslation } from "react-i18next"

const ProductList = props => {
  const {
    type,
    products,
    page,
    pagination,
    searchText = "",
    direction,
    sortBy,
    currentSubCategorySlug,
    loading = false,
    hideSort = false,
    preSaleSection,
    hasUpcomingSale = false
  } = props
  const { router } = useRouterClient()
  const { searchParams } = useParams()
  const { onSaleColor } = useLayoutContext()
  const { t } = useTranslation()

  const handleChangePage = nextPage => {
    const newParams = new URLSearchParams(searchParams)
    newParams.set("page", nextPage)
    if (direction) {
      newParams.set("direction", direction)
    }
    if (sortBy) {
      newParams.set("sortBy", sortBy)
    }
    if (searchText) {
      newParams.set("keyword", searchText)
    }
    if (currentSubCategorySlug?.length > 0) {
      const removeAll = currentSubCategorySlug.filter(item => item !== "all")
      if (removeAll.length > 0) {
        newParams.set("keyword", removeAll.join(","))
      }
    }
    router.push(`?${newParams.toString()}`)
  }

  const handleChangeFilter = ({
    key,
    value,
    direction: nextDirection = "asc"
  }) => {
    const newParams = new URLSearchParams(searchParams)
    newParams.set("page", 1)
    newParams.set(key, value)
    newParams.set("direction", nextDirection)
    if (searchText) {
      newParams.set("keyword", searchText)
    }
    if (currentSubCategorySlug?.length > 0) {
      const removeAll = currentSubCategorySlug.filter(item => item !== "all")
      if (removeAll.length > 0) {
        newParams.set("keyword", removeAll.join(","))
      }
    }
    router.push(`?${newParams.toString()}`)
  }

  const renderProductList = () => {
    if (products?.length === 0) {
      if (type === COLLECTION.ON_SALE) {
        if (hasUpcomingSale) return null
        return <EmptyProductSale isOnSale={true} />
      }
      return <EmptyProduct />
    }
    return products?.map((product, index) => (
      <Grid key={product?.id || index} item xs={6} lg={3}>
        <ProductCard product={product} />
      </Grid>
    ))
  }

  return (
    <AppContainer
      sx={{
        padding: { xs: "0 16px !important", md: "0 24px !important" }
      }}
    >
      <SectionContainer
        maxWidth={true}
        sxWrapper={{
          margin: 0
        }}
        sx={{
          flexDirection: "column",
          paddingTop: 0
        }}
      >
        <Box>
          <ProductTitle
            searchText={searchText}
            type={type}
            onSaleColor={onSaleColor}
          />
        </Box>
        {type === COLLECTION.ON_SALE && preSaleSection}
        {!hideSort && (
          <Stack
            gap={2}
            my={{
              xs: 2,
              md: 3
            }}
            alignItems="center"
          >
            <Icons name="sort" />
            <Typography variant="body1">{t("sort_by")}</Typography>
            <Filter value={sortBy} onChange={handleChangeFilter} />
          </Stack>
        )}
        {loading ? (
          <Box mt={hideSort ? 0 : -3}>
            <ProductListSkeleton />
          </Box>
        ) : (
          <Grid
            mt={hideSort ? 0 : -3}
            container
            spacing={{
              xs: 1,
              md: 3
            }}
            sx={{
              marginLeft: {
                xs: "-8px",
                md: "-24px"
              },
              width: "100%"
            }}
          >
            {renderProductList()}
            <Grid
              mt={{
                xs: 0,
                md: 3
              }}
              xs={12}
            >
              {pagination &&
                Math.ceil(pagination?.total / PRODUCT_PAGE_SIZE) > 1 && (
                  <Pagination
                    page={page}
                    total={Math.ceil(pagination?.total / PRODUCT_PAGE_SIZE)}
                    onChange={handleChangePage}
                  />
                )}
            </Grid>
          </Grid>
        )}
      </SectionContainer>
    </AppContainer>
  )
}

export default ProductList
